import { InertListItem as InertListItemType } from '@/schemas/sceneDataSchema';
import { Box, Icon, styled, Typography } from '@mui/material';
import { TypographyColors } from '@/constants/typographyTypes';
import { ChipColors, ChipSizes, ChipVariant } from '@/constants/chipTypes';
import { InertChip } from '@/components/DynamicUI/InertChip';
import { IconColors } from '@/constants/iconTypes';

interface InertListItemProps {
  component: InertListItemType;
}

export const InertListItem: React.FC<InertListItemProps> = ({
  component: {
    icon,
    icon_color,
    overline,
    label1,
    label2,
    supporting_text,
    with_dividers,
    trailing_text,
    trailing_pill,
  },
}) => {
  return (
    <StyledListItem withDividers={!!with_dividers}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.primary' }}>
        {icon && <Icon {...(icon_color && icon_color !== IconColors.TEXT && { color: icon_color })}>{icon}</Icon>}
        <TextGroup>
          {overline && (
            <Typography
              variant='overline'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {overline}
            </Typography>
          )}
          <Typography variant='body1'>{label1}</Typography>
          {label2 && <Typography variant='body1'>{label2}</Typography>}
          {supporting_text && (
            <Typography
              variant='body2'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {supporting_text}
            </Typography>
          )}
        </TextGroup>
      </Box>
      <Box sx={{ paddingRight: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {trailing_pill && (
          <InertChip
            label={trailing_pill}
            size={ChipSizes.SMALL}
            color={ChipColors.TEXT}
            variant={ChipVariant.FILLED}
          />
        )}
        {trailing_text && (
          <Typography
            variant='body2'
            textAlign={'right'}
            color={TypographyColors.TEXT_SECONDARY}
            style={{ lineHeight: 'unset' }}
          >
            {trailing_text}
          </Typography>
        )}
      </Box>
    </StyledListItem>
  );
};

const StyledListItem = styled('div')<{ withDividers?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 12px;
  /* Handle optional dividers between ListItems */
  ${({ withDividers }) => withDividers && 'border-bottom: 1px solid var(--mui-palette-border-main);'}
  &:last-of-type {
    border-bottom: none;
  }
  /* Animate chevron icon */
  .chevron-right {
    transition: transform 0.2s var(--ease-out-quart);
  }
  &:hover {
    .chevron-right {
      transform: translateX(4px);
    }
  }
`;

const TextGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
  & > span,
  & > p {
    line-height: 1;
  }
`;
